import React, { FC, useState } from 'react';

import { noop } from 'lodash';
import { AxiosInstance } from 'axios';
import { createBridgeComponent } from 'mfe-react-bridge/react17';
import { Button, Text } from 'mintel-react-ui';

import { CustomPanelType, HandlePresentType } from '../../../types';
import useTranslate from '../../../utils/translations';
import { useCwaAxios } from '../../../utils/cwaAxios';
import useTracking from '../../../utils/useTracking';
import styles from './PresentPanel.scss';
import fileDownload from '../fileDownload';

type PresentPanelSectionProps = {
  header: string;
  body: string;
  enablePresent: () => void;
  text: string;
  downloadPath?: string;
  trackDownload: () => void;
  cwaAxiosTaskbar: AxiosInstance;
};

const PresentPanelSection: FC<PresentPanelSectionProps> = ({
  header,
  body,
  enablePresent,
  text,
  downloadPath,
  trackDownload,
  cwaAxiosTaskbar,
}) => {
  const [buttonStatus, setButtonStatus] = useState(Button.Status.DEFAULT);

  return (
    <div>
      <Text.Heading5 darkBg tagName="h5" underlineLinks>
        {header}
      </Text.Heading5>
      <Text.Body darkBg tagName="p" underlineLinks>
        {body}
      </Text.Body>
      <div className={styles.buttons}>
        <div className={styles.button}>
          <Button.ActionButton handleClick={enablePresent} fullWidth>
            {text}
          </Button.ActionButton>
        </div>
        {downloadPath && (
          <div className={styles.button}>
            <Button.DownloadButton
              handleClick={(): Promise<void> => {
                setButtonStatus(Button.Status.LOADING);
                trackDownload();
                return fileDownload(cwaAxiosTaskbar, downloadPath)
                  .then(() => setButtonStatus(Button.Status.SUCCESS))
                  .catch(() => setButtonStatus(Button.Status.ERROR));
              }}
              status={buttonStatus}
              fullWidth
            />
          </div>
        )}
      </div>
    </div>
  );
};

const usePresentPanel = (
  handlePresent: HandlePresentType,
  downloadPath: string | undefined,
): CustomPanelType => {
  const t = useTranslate();
  const cwaAxiosTaskbar = useCwaAxios();
  const { trackPresentPanelDownload } = useTracking();

  return {
    bridge: {
      default: createBridgeComponent({
        rootComponent: (): JSX.Element => (
          <>
            <PresentPanelSection
              header={t('Keynote')}
              body={t(
                'The keynote presentation is an impactful, light version of this' +
                  ' content that respects all presentation settings from our CMS',
              )}
              enablePresent={(): void => handlePresent()}
              text={t('Present Keynote')}
              downloadPath={
                downloadPath && `${downloadPath}/keynote-pptx-async`
              }
              cwaAxiosTaskbar={cwaAxiosTaskbar}
              trackDownload={trackPresentPanelDownload}
            />
            <div className={styles.spacing} />
            <PresentPanelSection
              header={t('Full written')}
              body={t(
                'The full written version keeps all of the content in a' +
                  ' presentation-ready layout.',
              )}
              enablePresent={(): void => handlePresent(true)}
              text={t('Present Full Written')}
              downloadPath={
                downloadPath && `${downloadPath}/full-written-pptx-async`
              }
              cwaAxiosTaskbar={cwaAxiosTaskbar}
              trackDownload={trackPresentPanelDownload}
            />
          </>
        ),
      }),
    },
    closeSidebar: noop,
    iconName: 'present2',
    title: t('Present'),
    subtitle: t(
      'Here you can present online or download presentation-ready versions of this content piece.',
    ),
    color: 'primary',
    width: 400,
    id: 'present-panel',
  };
};

export default usePresentPanel;
